import { useEffect, useMemo } from 'react';
import type { ProductEventDataList, RecommendationsSliderTypeKey } from 'corgi-types';

import { ProductEvent } from 'common/enums/Tracking';
import { articleToTrackingProps } from 'common/tracking/articleToTrackingProps';
import { dispatchTrackingEventGA4 } from 'common/tracking/dispatchTrackingEventGA4';
import { getProductViewEventData } from 'common/tracking/productEventData';
import type { RecommendationItemProps } from 'common/types/recommendation';
import { getListEventValue } from '../utils';

interface UseProductViewTrackingEventOptions {
  items: RecommendationItemProps[];
  type?: RecommendationsSliderTypeKey;
  activeSlideIndex: number;
  shownSlides: number;
  eventDataList?: ProductEventDataList;
  isObserved: boolean;
}

interface SkuIndexPair {
  articleSku: string;
  index: number;
}

interface GetUntrackedSkuIndexPairsOptions extends Pick<
  UseProductViewTrackingEventOptions,
  'items' | 'activeSlideIndex' | 'shownSlides'
> {
  shownSkus: Set<string>;
}

interface GetProductViewImpressionsOptions extends Pick<
  UseProductViewTrackingEventOptions,
  'type' | 'items' | 'eventDataList'
> {
  skuIndexPairsUntracked: SkuIndexPair[];
}

const getUntrackedSkuIndexPairs = ({
  items,
  shownSkus,
  activeSlideIndex,
  shownSlides,
}: GetUntrackedSkuIndexPairsOptions) => {
  const shownSkuIndexPairs = items
    .slice(activeSlideIndex, activeSlideIndex + shownSlides)
    .map(({ article: { sku: articleSku } }, index) => ({
      articleSku,
      index: index + activeSlideIndex,
    }));

  return shownSkuIndexPairs.filter(({ articleSku }) => !shownSkus.has(articleSku));
};

const getProductViewImpressions = ({
  type,
  items,
  eventDataList,
  skuIndexPairsUntracked,
}: GetProductViewImpressionsOptions) => {
  const list = getListEventValue({ eventDataList, type });

  return skuIndexPairsUntracked.map(({ index }) =>
    getProductViewEventData({
      list,
      position: index,
      tracking: articleToTrackingProps(items[index].article),
      ...(items[index].dyDecisionId && {
        dyDecisionId: items[index].dyDecisionId,
      }),
      ...(items[index].dySlotId && {
        dySlotId: items[index].dySlotId,
      }),
    })
  );
};

export const useProductViewEventTracker = ({
  type,
  items,
  activeSlideIndex,
  shownSlides,
  eventDataList,
  isObserved,
}: UseProductViewTrackingEventOptions) => {
  const shownSkus = useMemo(() => new Set<string>(), []);
  const skuIndexPairsUntracked = useMemo(
    () =>
      getUntrackedSkuIndexPairs({
        items,
        shownSkus,
        activeSlideIndex,
        shownSlides,
      }),
    [items, shownSkus, activeSlideIndex, shownSlides]
  );
  const productViewImpressions = useMemo(
    () =>
      getProductViewImpressions({
        type,
        items,
        eventDataList,
        skuIndexPairsUntracked,
      }),
    [type, items, eventDataList, skuIndexPairsUntracked]
  );

  useEffect(() => {
    if (isObserved) {
      if (productViewImpressions.length > 0) {
        dispatchTrackingEventGA4({
          event: ProductEvent.View,
          item_list_name: productViewImpressions[0].item_list_name,
          items: productViewImpressions,
        });
        for (const { articleSku } of skuIndexPairsUntracked) {
          shownSkus.add(articleSku);
        }
      }
    }
  }, [isObserved, productViewImpressions, skuIndexPairsUntracked]);
};
